import type { StudioItem, StudioRunGeometry } from "./studioTypes";
import { placementBayOf } from "./basePogWalk";

export const NONE_VALUE = "(none)";

export interface TreeKid {
  value: string;
  count: number;
  node: TreeNode | null;
}

export interface TreeNode {
  attr: string;
  reading: "vertical" | "horizontal";
  purity: number;
  gain: number;
  k: number;
  direction: "" | " ascending" | " descending";
  kind: "block" | "order";

  grouping: boolean;
  kids: readonly TreeKid[];

  seam: readonly string[];
}

interface SeqItem {
  skuId: string;
  bay: number;
  row: number;
  name: string;
}

const numericOf = (value: string): number | null => {
  const match = value.match(/[\d.]+/);
  if (!match) return null;

  const parsed = parseFloat(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
};

const compactNumber = (value: number): string => {
  if (Number.isInteger(value)) return String(value);
  return String(Math.round(value * 1e6) / 1e6);
};

const entropyOf = (counts: readonly number[]): number => {
  const total = counts.reduce((sum, count) => sum + count, 0);
  if (total === 0) return 0;
  let h = 0;
  for (const count of counts) {
    if (count <= 0) continue;
    const p = count / total;
    h -= p * Math.log2(p);
  }
  return h;
};

const seqOf = (items: readonly StudioItem[], runs: readonly StudioRunGeometry[]): SeqItem[] => {
  const runOf = new Map(runs.map((run) => [run.run, run]));
  const compareKeys = (a: readonly number[], b: readonly number[]): number => {
    for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
      const diff = (a[index] ?? 0) - (b[index] ?? 0);
      if (diff !== 0) return diff;
    }
    return 0;
  };
  const withKey = items.map((item) => {
    let best: { key: readonly number[]; row: number; bay: number } | null = null;
    for (const placement of item.placements) {
      const bay = placementBayOf(placement, runOf.get(placement.run));
      const key = [placement.run, bay, placement.shelf, placement.xCm] as const;
      if (!best || compareKeys(key, best.key) < 0) best = { key, row: placement.shelf, bay };
    }
    return { item, best };
  });
  const placed = withKey.filter((entry) => entry.best !== null);
  placed.sort((a, b) => compareKeys(a.best!.key, b.best!.key));
  return placed.map((entry) => ({
    skuId: entry.item.skuId,
    bay: entry.best!.bay,
    row: entry.best!.row,
    name: entry.item.name,
  }));
};

const cutBaseOf = (key: string): string | null => {
  const index = key.indexOf(" >= ");
  return index < 0 ? null : key.slice(0, index);
};

const MIN_PURITY = 0.75;
const CLEAN_PURITY = 0.95;
const MAX_DEPTH = 6;

interface Score {
  attr: string;
  tag: "vertical" | "horizontal";
  purity: number;
  gain: number;
  k: number;
  direction: "" | " ascending" | " descending";
  order: readonly string[];
  excused: readonly number[];
  clean: boolean;
  precedence: number;
  inBlock: ReadonlySet<number>;

  grouping: boolean;
}

export const inferTree = (
  nodeItems: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  attrs: readonly string[],
  attrOf: (item: StudioItem, attr: string) => string,
): TreeNode | null => {
  const seq = seqOf(nodeItems, runs);
  const itemOf = new Map(nodeItems.map((item) => [item.skuId, item]));
  const n = seq.length;
  if (n === 0) return null;

  const A = new Map<string, string[]>();
  // Cuts let continuous measures participate without making every number a category.
  for (const attr of attrs) {
    A.set(
      attr,
      seq.map((entry) => attrOf(itemOf.get(entry.skuId)!, attr)),
    );
  }
  const precedenceOf = new Map(attrs.map((attr, index) => [attr, index]));

  for (const attr of [...attrs]) {
    const values = A.get(attr)!;
    const magnitudes = values.map(numericOf);
    if (magnitudes.some((m) => m === null) || new Set(magnitudes).size < 3) continue;
    const distinct = [...new Set(magnitudes as number[])].sort((a, b) => a - b);
    for (const threshold of distinct.slice(1)) {
      const label = compactNumber(threshold);
      const name = `${attr} >= ${label}`;
      A.set(
        name,
        magnitudes.map((m) => (m! >= threshold ? `>= ${label}` : `< ${label}`)),
      );
      precedenceOf.set(name, precedenceOf.get(attr) ?? 0);
    }
  }

  const rowOf = seq.map((entry) => entry.row);
  const bayOfIndex = seq.map((entry) => entry.bay);

  const score = (attr: string, group: readonly number[]): Score | null => {
    const values = A.get(attr)!;
    const counts = new Map<string, number>();
    for (const index of group) counts.set(values[index], (counts.get(values[index]) ?? 0) + 1);
    const k = counts.size;
    if (k < 2) return null;

    const rows = new Map<number, number[]>();
    for (const index of [...group].sort((a, b) => a - b)) {
      const line = rows.get(rowOf[index]) ?? [];
      line.push(index);
      rows.set(rowOf[index], line);
    }
    const seam = new Set<number>();
    for (const line of rows.values()) {
      seam.add(line[0]);
      seam.add(line[line.length - 1]);
    }

    const bayMajorOrder = [...group].sort((a, b) => a - b);
    const rowMajorOrder = [...group].sort((a, b) => rowOf[a] - rowOf[b] || a - b);

    const numeric = [...counts.keys()].every((value) => numericOf(value) !== null);
    interface Ladder {
      reading: "horizontal" | "vertical";
      direction: " ascending" | " descending";
      fit: Set<number>;
    }
    let ladder: Ladder | null = null;
    if (numeric) {
      const readingOrders: readonly ["horizontal" | "vertical", readonly number[]][] = [
        ["horizontal", bayMajorOrder],
        ["vertical", rowMajorOrder],
      ];
      outer: for (const [readingName, order] of readingOrders) {
        const core = order
          .filter((index) => !seam.has(index))
          .map((index) => numericOf(values[index])!);
        const comparators: readonly [
          (x: number, y: number) => boolean,
          " ascending" | " descending",
        ][] = [
          [(x, y) => x <= y, " ascending"],
          [(x, y) => x >= y, " descending"],
        ];
        for (const [cmp, dname] of comparators) {
          if (core.length < 2) continue;
          let monotone = true;
          for (let i = 0; i + 1 < core.length; i += 1) {
            if (!cmp(core[i], core[i + 1])) {
              monotone = false;
              break;
            }
          }
          if (!monotone) continue;
          const fit = new Set(order.filter((index) => !seam.has(index)));
          order.forEach((index, pos) => {
            if (!seam.has(index)) return;
            let prev: number | null = null;
            for (let p = pos - 1; p >= 0; p -= 1) {
              if (fit.has(order[p])) {
                prev = numericOf(values[order[p]]);
                break;
              }
            }
            let next: number | null = null;
            for (let p = pos + 1; p < order.length; p += 1) {
              if (fit.has(order[p])) {
                next = numericOf(values[order[p]]);
                break;
              }
            }
            const value = numericOf(values[index])!;
            if ((prev === null || cmp(prev, value)) && (next === null || cmp(value, next)))
              fit.add(index);
          });
          ladder = { reading: readingName, direction: dname, fit };
          break outer;
        }
      }
    }

    const inBlock = new Set<number>();
    const blockRows = new Map<string, [number, number]>();
    if (ladder) {
      for (const index of ladder.fit) inBlock.add(index);
      for (const value of counts.keys()) {
        const fitted = group.filter((index) => values[index] === value && ladder!.fit.has(index));
        const its = fitted.length > 0 ? fitted : group.filter((index) => values[index] === value);
        blockRows.set(value, [
          Math.min(...its.map((index) => rowOf[index])),
          Math.max(...its.map((index) => rowOf[index])),
        ]);
      }
    } else {
      for (const value of counts.keys()) {
        const runs = new Map<number, number[]>();
        for (const [row, line] of rows) {
          let best: number[] = [];
          let current: number[] = [];
          for (const index of line) {
            current = values[index] === value ? [...current, index] : [];
            if (current.length > best.length) best = current;
          }
          if (best.length > 0) runs.set(row, best);
        }
        const bayRangeOf = (run: readonly number[]): [number, number] => {
          const bays = run.map((index) => bayOfIndex[index]);
          return [Math.min(...bays), Math.max(...bays)];
        };
        const sortedRows = [...runs.keys()].sort((a, b) => a - b);

        const groups: number[][] = [];
        let current: number[] = [];
        for (const row of sortedRows) {
          if (current.length > 0) {
            const last = current[current.length - 1];
            const [loLast, hiLast] = bayRangeOf(runs.get(last)!);
            const [loRow, hiRow] = bayRangeOf(runs.get(row)!);
            const overlaps = loRow <= hiLast && hiRow >= loLast;
            if (row !== last + 1 || !overlaps) {
              groups.push(current);
              current = [];
            }
          }
          current.push(row);
        }
        if (current.length > 0) groups.push(current);
        if (groups.length === 0) continue;
        const weightOf = (g: readonly number[]) =>
          g.reduce((sum, row) => sum + (runs.get(row)?.length ?? 0), 0);
        const main = groups.reduce((best, candidate) =>
          weightOf(candidate) > weightOf(best) ? candidate : best,
        );

        if (weightOf(main) >= 2) {
          for (const row of main) for (const index of runs.get(row)!) inBlock.add(index);
        }
        blockRows.set(value, [main[0], main[main.length - 1]]);
      }
    }

    const single = new Set(group.filter((index) => counts.get(values[index]) === 1));
    const strays = group.filter(
      (index) => !inBlock.has(index) && !seam.has(index) && !single.has(index),
    );

    if (inBlock.size < 2) return null;
    const purity = 1 - strays.length / group.length;
    if (purity < MIN_PURITY) return null;

    let vertical = true;
    for (const line of rows.values()) {
      const distinctValues = new Set(
        line
          .filter((index) => inBlock.has(index) || !seam.has(index))
          .map((index) => values[index]),
      );
      if (distinctValues.size !== 1) {
        vertical = false;
        break;
      }
    }
    vertical =
      vertical && new Set([...blockRows.values()].map(([lo, hi]) => `${lo},${hi}`)).size > 1;

    const rowKeys = [...rows.keys()];
    const allRows = `${Math.min(...rowKeys)},${Math.max(...rowKeys)}`;
    vertical = vertical && ![...blockRows.values()].some(([lo, hi]) => `${lo},${hi}` === allRows);
    const tag: "vertical" | "horizontal" = vertical ? "vertical" : "horizontal";

    const firstAppearance = (order: readonly number[]): string[] => {
      const seen: string[] = [];
      const known = new Set<string>();
      for (const index of order) {
        const value = values[index];
        if (!known.has(value)) {
          known.add(value);
          seen.push(value);
        }
      }
      return seen;
    };
    const orders = {
      vertical: firstAppearance(rowMajorOrder),
      horizontal: firstAppearance(bayMajorOrder),
    };
    let direction: Score["direction"] = ladder?.direction ?? "";
    let monotoneReading: "vertical" | "horizontal" | null = ladder?.reading ?? null;
    if (!ladder) {
      for (const reading of ["vertical", "horizontal"] as const) {
        const nums = orders[reading].map((value) => numericOf(value)!);
        const ascending = nums.every((num, i) => i === 0 || num >= nums[i - 1]);
        const descending = nums.every((num, i) => i === 0 || num <= nums[i - 1]);
        if (numeric && ascending) {
          direction = direction || " ascending";
          monotoneReading = reading;
        } else if (numeric && descending) {
          direction = direction || " descending";
          monotoneReading = reading;
        }
      }
    }
    const appearance = ladder
      ? orders[ladder.reading]
      : direction && monotoneReading
        ? orders[monotoneReading]
        : orders[tag];

    const magnitudeOf = (value: string): number =>
      cutBaseOf(attr) ? (value.startsWith("<") ? 0 : 1) : (numericOf(value) ?? 0);
    const order =
      direction && numeric
        ? [...appearance].sort((a, b) =>
            direction === " ascending"
              ? magnitudeOf(a) - magnitudeOf(b)
              : magnitudeOf(b) - magnitudeOf(a),
          )
        : appearance;
    if (k > Math.ceil(group.length / 2) && !direction) return null;

    const gain = entropyOf([...counts.values()]) * (inBlock.size / group.length);
    const excused = group.filter((index) => !inBlock.has(index) && seam.has(index));
    return {
      attr,
      tag,
      purity,
      gain,
      k,
      direction,
      order,
      excused,
      clean: purity >= CLEAN_PURITY,
      precedence: precedenceOf.get(attr) ?? 0,
      inBlock,
      grouping: false,
    };
  };

  const bucketingOfANumber = (s: Score, group: readonly number[]): boolean => {
    if (s.direction || cutBaseOf(s.attr)) return true;
    const own = A.get(s.attr)!;
    for (const [other, otherValues] of A) {
      if (other === s.attr || cutBaseOf(other)) continue;
      const nums = group.map((index) => numericOf(otherValues[index]));
      if (nums.some((num) => num === null) || new Set(nums).size < 2) continue;
      const byNumber = group
        .map((index, position) => ({ index, num: nums[position]! }))
        .sort((a, b) => a.num - b.num)
        .map((entry) => own[entry.index]);
      let runs = 1;
      for (let i = 1; i < byNumber.length; i += 1) if (byNumber[i] !== byNumber[i - 1]) runs += 1;
      if (runs === new Set(byNumber).size) return true;
    }
    return false;
  };

  const greedyKey = (s: Score): readonly number[] => [
    s.clean && s.excused.length === 0 ? 1 : 0,
    s.clean ? 1 : 0,
    s.tag === "vertical" && s.clean ? 1 : 0,
    s.grouping ? 1 : 0,
    s.clean && s.direction !== "" ? 1 : 0,
    Math.round((s.gain / s.k) * 1000) / 1000,
    Math.round(s.gain * 1000) / 1000,
    -s.k,
    -s.precedence,
  ];
  const compareKeys = (a: readonly number[], b: readonly number[]): number => {
    for (let index = 0; index < a.length; index += 1) {
      if (a[index] !== b[index]) return a[index] - b[index];
    }
    return 0;
  };

  const grow = (
    group: readonly number[],
    pool: readonly string[],
    depth: number,
  ): TreeNode | null => {
    if (group.length < 3 || depth >= MAX_DEPTH) return null;
    const candidates = new Map<string, Score>();
    for (const attr of pool) {
      const s = score(attr, group);
      if (!s) continue;

      if (cutBaseOf(attr) && s.tag !== "vertical") continue;
      candidates.set(attr, s);
    }
    for (const attr of [...candidates.keys()]) {
      const base = cutBaseOf(attr);
      if (!base) continue;
      const ladder = candidates.get(base);
      if (ladder && ladder.clean && ladder.direction) candidates.delete(attr);
    }
    for (const s of candidates.values()) s.grouping = s.clean && !bucketingOfANumber(s, group);
    if (candidates.size === 0) return null;
    let best: Score | null = null;
    for (const s of candidates.values()) {
      if (!best || compareKeys(greedyKey(s), greedyKey(best)) > 0) best = s;
    }
    const winner = best!;
    const base = cutBaseOf(winner.attr) ?? winner.attr;
    const subPool = pool.filter(
      (attr) => attr !== winner.attr && (attr === base || cutBaseOf(attr) !== base),
    );
    const values = A.get(winner.attr)!;
    const kids: TreeKid[] = winner.order.map((value) => {
      const childGroup = group.filter((index) => values[index] === value);
      return { value, count: childGroup.length, node: grow(childGroup, subPool, depth + 1) };
    });
    const kind: TreeNode["kind"] =
      winner.k === 2 || kids.every((kid) => kid.count >= 2) ? "block" : "order";
    return {
      attr: winner.attr,
      reading: winner.tag,
      purity: winner.purity,
      gain: winner.gain,
      k: winner.k,
      direction: winner.direction,
      kind,
      grouping: winner.grouping,
      kids,
      seam: winner.excused.map((index) => seq[index].name.slice(0, 40)),
    };
  };

  const initialPool = attrs.filter((attr) => new Set(A.get(attr)!).size > 1);
  const cutPool = [...A.keys()].filter(
    (key) => !attrs.includes(key) && new Set(A.get(key)!).size > 1,
  );
  return grow(
    seq.map((_, index) => index),
    [...initialPool, ...cutPool],
    0,
  );
};
